import React, {
  RefObject,
  useEffect,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import SearchIcon from 'core/icons/search';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import {
  useTableReset,
} from 'core/hooks';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import ChargePolicyDialog, {
  ChargePolicyDialogParams,
  priceServiceOptions,
  priceServiceOptionsByType,
} from 'pages/Dashboard/pages/Billing/ChargeMaster/ChargePolicyDialog';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  pluralize,
} from '@populateui/utils';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import {
  PriceService,
} from 'pages/Dashboard/pages/Billing/types';
import CptCodesOverview from 'pages/Dashboard/pages/Billing/ChargeMaster/CptCodesOverview';
import CustomServicesOverview from 'pages/Dashboard/pages/Billing/ChargeMaster/CustomServicesOverview';

const tabs = priceServiceOptions.map(
  ({ label, ...option }) => ({ ...option, label: pluralize(label, 0, false) }),
);

export default function ChargeMaster() {
  const {
    reset,
    tableInstanceRef,
  } = useTableReset();
  const { type = 'cpt' } = useParams<{ type?: PriceService }>();
  const navigate = useNavigate();

  const isCptCodes = type === 'cpt';
  const [priceDialogParams, setPriceDialogParams] = useState<ChargePolicyDialogParams | null>(null);
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery();
  const Overview = isCptCodes ? CptCodesOverview : CustomServicesOverview;

  useEffect(() => {
    const unsubscribe = billingEventChannel.on('onPriceDialogOpen', (billingChargeInfo) => {
      setPriceDialogParams(billingChargeInfo);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <div className="flex flex-col space-y-4 h-full w-full">
      <div className="flex items-center justify-between px-4">
        <div className="flex gap-4">
          <NavigationTabs
            className="!min-h-[initial]"
            classes={{
              tab: '!font-semibold hover:!text-blue-500',
              active: '!text-blue-500 !border-blue-500',
            }}
            tabs={tabs}
            activeIndex={Number(!isCptCodes)}
            onClick={(index) => {
              if (priceServiceOptions[index]?.key !== type) {
                navigate(`/billing/charge-master/${priceServiceOptions[index]?.key}`);
                setSearchQuery('');
              }
            }}
          />
          <SearchInput
            placeholder="Search for Services"
            className="w-[18.5rem]"
            value={searchQuery}
            onChange={(query) => {
              setSearchQuery(query);
              reset();
            }}
            inputProps={{ autoComplete: 'off', maxLength: maxBillingCodeSearchQueryLength }}
            icon={<SearchIcon className="search-icon !text-sm !w-3.5 !h-3.5 text-gray-400" />}
            id="services-search"
          />
        </div>
        <Button
          className="!h-8"
          variant="contained"
          type="button"
          color="primary"
          startIcon={<AddIcon className="!w-4 !h-4" />}
          onClick={() => {
            setPriceDialogParams({ type: priceServiceOptionsByType[type].index });
          }}
        >
          Add Charge Policy
        </Button>
      </div>
      <Overview
        tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
        searchQuery={searchQuery}
      />
      {!isNil(priceDialogParams) && (
        <ChargePolicyDialog
          {...priceDialogParams}
          onClose={() => { setPriceDialogParams(null); }}
        />
      )}
    </div>
  );
}
